import React, { useState, useParams, useEffect, useMemo, useCallback } from 'react';
import moment from 'moment';
import QRCode from 'qrcode'; // eslint-disable-line import/no-extraneous-dependencies
import { 
  Button, Card, Checkbox, Descriptions, Form, Input, Modal, Typography,
  Table, Tabs, Tag, Tooltip, Empty, Select, Space, Radio, message, Avatar,
  DatePicker, Popconfirm, Divider, InputNumber, Switch, Badge, Row, Col,
} from 'antd';
import { 
  CheckCircleFilled, ClockCircleTwoTone, CloseCircleFilled, RedoOutlined,
  CopyOutlined, EyeOutlined, EyeInvisibleOutlined, EditOutlined,
  UserOutlined, TeamOutlined, ExportOutlined, WarningFilled,
  PlusCircleOutlined, MinusCircleOutlined, ArrowRightOutlined,
  PlusOutlined, DeleteOutlined, SettingOutlined,
  DownloadOutlined, AppleFilled,
  DollarCircleOutlined, PercentageOutlined, CalculatorOutlined,
  QuestionCircleOutlined, SafetyCertificateOutlined,
  ContactsOutlined,
} from '@ant-design/icons';

import WebhookModel from '../../models/WebhookModel';
import WebhooksModel from '../../models/WebhooksModel';
import StrikeTemplateTagModel from '../../models/StrikeTemplateTagModel';
import { PROCESSOR_KEYS } from '../../models/useAdminProcessors';
import { usePartnerTags } from '../../models/usePartnerTags';
import { usePartnerTemplates } from '../../models/usePartnerTemplates';
import { TEST_MODE_CLIENT_COLOR } from '../client/TestModeBanner';
import { useWebookConfigs } from '../../models/useWebhookConfigs';
import { useWebookEvents } from '../../models/useWebhookEvents';
import { useClient } from '../../models/useClient';
import { StrikeColors } from '../../utils/Colors';
import ProductSwitcher from '../../utils/ProductSwitcher';
import { colorForBackgroundColor, generateSelectedColor, rgbColorToHex } from '../../utils/RGBColorToHex';
import img_strikepay_logo from '../../images/strikepay_Mark_RGB_Black.png'; // eslint-disable-line
import img_striketip_logo from '../../images/striketip_logo.png'; // eslint-disable-line
import img_qr_logo from '../../images/strikepay_qr_icon.png'; // eslint-disable-line
import img_mary from '../../images/mary.png'; // eslint-disable-line
import img_jim from '../../images/jim.png'; // eslint-disable-line
import CenteredSpinner from '../CenteredSpinner';
import { TemplateTagImageEditor } from '../client/UploadableImageEditor';
import { TeamEditPaymentDetailsFeesFormItem } from '../client/TeamPaymentDetailsSettings';
import { EditFieldSettingsView } from '../client/EditFieldSettingsView';
import { useClientDetails } from '../client/useClientDetails';
import MoneyTitle from '../customer/MoneyTitle';
import ContrastColor from '../customer/ContrastColor';
import { getAppBaseUrl, getPayerBaseUrl } from '../../utils/URLBuilder';

import { ColorPickerControlFormItem } from './AdminSitesColorPicker';

import '../../App.scss';
import 'antd/dist/antd.css';

const { TabPane } = Tabs;
const { Search } = Input;
const { Text, Paragraph, Title } = Typography;
const { RangePicker } = DatePicker;

const isLocalHost = window.location.hostname === 'localhost';
const payerBaseURL = getPayerBaseUrl();

const TemplateValuesEditor = ({ form, currency }) => {
  const [newValue, setNewValue] = useState('');
  const values = form.getFieldValue('values') || [];
  const selectedValueByIndex = form.getFieldValue('selectedValueByIndex');

  const handleAdd = () => {
    const val = Math.round(parseFloat(newValue) * 100);
    if (!isNaN(val) && !values.includes(val) && values.length < 5) {
      const newValues = [...values, val].sort((a, b) => a - b);
      form.setFieldsValue({ values: newValues });
      setNewValue('');
    }
  };

  const handleDelete = (index) => {
    const newValues = values.filter((_, i) => i !== index);
    form.setFieldsValue({ values: newValues });
    
    // Adjust default index if needed
    if (selectedValueByIndex >= newValues.length) {
      form.setFieldsValue({ selectedValueByIndex: Math.max(0, newValues.length - 1) });
    }
  };

  const handleSetDefault = (index) => {
    form.setFieldsValue({ selectedValueByIndex: index });
  };

  const handleEdit = (index, newVal) => {
    const roundedNewVal = Math.round(newVal * 100);
    if (!isNaN(roundedNewVal)) {
      const newValues = [...values];
      newValues[index] = roundedNewVal;
      form.setFieldsValue({ values: newValues });
    }
  };

  const columns = [
    {
      title: 'Pre-set Amounts',
      key: 'amount',
      render: (_, record, index) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <InputNumber 
            value={record / 100} 
            onChange={(val) => handleEdit(index, val)}
            precision={2}
            step={1}
            style={{ width: '100px', fontWeight: 'bold' }}
            controls={false}
          />
          <Space>
            <Popconfirm title="Delete this amount?" onConfirm={() => handleDelete(index)}>
              <Button type="text" danger icon={<MinusCircleOutlined />} />
            </Popconfirm>
            <Button 
              type={index === selectedValueByIndex ? "primary" : "default"}
              onClick={() => handleSetDefault(index)}
              size="small"
            >
              {index === selectedValueByIndex ? "Default" : "Set Default"}
            </Button>
          </Space>
        </div>
      )
    }
  ];

  return (
    <div style={{ padding: '16px 0' }}>
      <Title level={5}>Set up Pre-set Amounts</Title>
      <Table 
        dataSource={values} 
        columns={columns} 
        pagination={false} 
        size="small"
        rowKey={(_, index) => `val-${index}`}
        locale={{ emptyText: 'No amounts added' }}
      />
      <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
        <Input 
          type="number" 
          placeholder="Add amount" 
          value={newValue} 
          onChange={e => setNewValue(e.target.value)}
          style={{ width: '120px' }}
        />
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={handleAdd}
          disabled={!newValue || values.length >= 5}
        >
          Add
        </Button>
      </div>
    </div>
  );
};

const TemplatePercentsEditor = ({ form }) => {
  const [newValue, setNewValue] = useState('');
  const percents = form.getFieldValue('percents') || [];
  const selectedValueByIndex = form.getFieldValue('selectedValueByIndex');

  const handleAdd = () => {
    const val = Math.round(parseFloat(newValue) * 100);
    if (!isNaN(val) && !percents.includes(val) && percents.length < 5) {
      const newPercents = [...percents, val].sort((a, b) => a - b);
      form.setFieldsValue({ percents: newPercents });
      setNewValue('');
    }
  };

  const handleDelete = (index) => {
    const newPercents = percents.filter((_, i) => i !== index);
    form.setFieldsValue({ percents: newPercents });

    // Adjust default index if needed
    if (selectedValueByIndex >= newPercents.length) {
      form.setFieldsValue({ selectedValueByIndex: Math.max(0, newPercents.length - 1) });
    }
  };

  const handleSetDefault = (index) => {
    form.setFieldsValue({ selectedValueByIndex: index });
  };

  const handleEdit = (index, newVal) => {
    const roundedNewVal = Math.round(newVal * 100);
    if (!isNaN(roundedNewVal)) {
      const newPercents = [...percents];
      newPercents[index] = roundedNewVal;
      form.setFieldsValue({ percents: newPercents });
    }
  };

  const columns = [
    {
      title: 'Percentages',
      key: 'percent',
      render: (_, record, index) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <InputNumber 
            value={record / 100} 
            onChange={(val) => handleEdit(index, val)}
            precision={2}
            step={0.1}
            style={{ width: '100px', fontWeight: 'bold' }}
            formatter={value => `${value}%`}
            parser={value => value.replace('%', '')}
            controls={false}
          />
          <Space>
            <Popconfirm title="Delete this percentage?" onConfirm={() => handleDelete(index)}>
              <Button type="text" danger icon={<MinusCircleOutlined />} />
            </Popconfirm>
            <Button 
              type={index === selectedValueByIndex ? "primary" : "default"}
              onClick={() => handleSetDefault(index)}
              size="small"
            >
              {index === selectedValueByIndex ? "Default" : "Set Default"}
            </Button>
          </Space>
        </div>
      )
    }
  ];

  return (
    <div style={{ padding: '16px 0' }}>
      <Title level={5}>Set up Percentages</Title>
      <Table 
        dataSource={percents} 
        columns={columns} 
        pagination={false} 
        size="small"
        rowKey={(_, index) => `pct-${index}`}
        locale={{ emptyText: 'No percentages added' }}
      />
      <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
        <Input 
          type="number" 
          placeholder="Add percentage" 
          value={newValue} 
          onChange={e => setNewValue(e.target.value)}
          style={{ width: '120px' }}
        />
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={handleAdd}
          disabled={!newValue || percents.length >= 5}
        >
          Add
        </Button>
      </div>
    </div>
  );
};

const TemplateTipEditorModal = ({ visible, onCancel, template, isAdmin }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      const pd = template.paymentDetails || {};
      // Apply business rules for fees:
      // shouldAddOnFees is null => defaults to true
      // shouldShowFeesOption is null => defaults to true
      const shouldAddOnFees = pd.shouldAddOnFees ?? true;
      const shouldShowFeesOption = pd.shouldShowFeesOption ?? true;

      form.setFieldsValue({
        values: template.values || [],
        percents: template.percents || [],
        selectedValueByIndex: template.selectedValueByIndex || 0,
        paymentDetails: {
          ...pd,
          shouldAddOnFees,
          shouldShowFeesOption,
          fieldSettings: pd.fieldSettings || []
        },
      });
    }
  }, [visible, template, form]);

  const onFinish = async (formValues) => {
    setLoading(true);
    try {
      const model = new StrikeTemplateTagModel({ 
        issuerId: template.issuerId || 'strike', 
        templateTagId: template.id 
      });

      const values = { ...form.getFieldsValue(true), ...formValues };

      const updatedData = {
        partnerClientId: template.partnerClientId || null, 
        values: Array.isArray(values.values) ? values.values : (Array.isArray(template.values) ? template.values : []),
        percents: Array.isArray(values.percents) ? values.percents : (Array.isArray(template.percents) ? template.percents : []),
        selectedValueByIndex: values.selectedValueByIndex !== undefined ? values.selectedValueByIndex : (template.selectedValueByIndex || 0),
        paymentDetails: {
          ...(template.paymentDetails || {}),
          ...(values.paymentDetails || {}),
          shouldAddOnFees: values.paymentDetails?.shouldAddOnFees ?? true,
          shouldShowFeesOption: values.paymentDetails?.shouldShowFeesOption ?? true,
          fieldSettings: values.paymentDetails?.fieldSettings || template.paymentDetails?.fieldSettings || []
        },
      };

      await model.updateTagWithData(updatedData);
      message.success('Payment settings updated successfully');
      onCancel();
    } catch (error) {
      console.error('Update template error:', error);
      message.error('Failed to update payment settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={`Edit Payment Settings: ${template.id}`}
      visible={visible}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={700}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item name="values" hidden />
        <Form.Item name="percents" hidden />
        <Form.Item name="selectedValueByIndex" hidden />
        <Tabs type="card" size="small">
          <TabPane tab={<span><DollarCircleOutlined /> Values</span>} key="tip-values">
            <Card size="small" style={{ margin: '16px 0', borderRadius: '8px' }}>
              <Form.Item noStyle shouldUpdate={(prev, curr) => prev.values !== curr.values || prev.selectedValueByIndex !== curr.selectedValueByIndex}>
                {() => <TemplateValuesEditor form={form} currency={template.paymentDetails?.currency || 'EUR'} />}
              </Form.Item>
            </Card>
          </TabPane>
          <TabPane tab={<span><PercentageOutlined /> Percentages</span>} key="tip-percents">
            <Card size="small" style={{ margin: '16px 0', borderRadius: '8px' }}>
              <Form.Item noStyle shouldUpdate={(prev, curr) => prev.percents !== curr.percents || prev.selectedValueByIndex !== curr.selectedValueByIndex}>
                {() => <TemplatePercentsEditor form={form} />}
              </Form.Item>
            </Card>
          </TabPane>
          <TabPane tab={<span><CalculatorOutlined /> Payment Details</span>} key="tip-payment">
            <Card size="small" style={{ margin: '16px 0', borderRadius: '8px' }}>
              <div style={{ padding: '8px 0' }}>
                <TeamEditPaymentDetailsFeesFormItem team={template} form={form} />
                <Divider style={{ margin: '12px 0' }} />
                <EditFieldSettingsView form={form} />
              </div>
            </Card>
          </TabPane>
        </Tabs>
      </Form>
    </Modal>
  );
};

const LinkAndQRSection = ({ type, baseUrl, uid, setUid, fgHex, bgHex, id, copyToClipboard, getQRImage, downloadQR, qrImages, hasCustomFgColor }) => {
  const isSingle = type === 'single';
  const displayUrl = isSingle ? `${baseUrl}?uid=${uid || 'uuu-uuu'}` : baseUrl;
  const qrTargetUrl = isSingle ? `${baseUrl}?qr=${uid || 'uuu-uuu'}` : baseUrl;

  const fgLabelPrefix = hasCustomFgColor ? 'Brand FG' : 'Smart FG';

  return (
    <div style={{ textAlign: 'left', marginBottom: isSingle ? 0 : '16px' }}>
      <Text type="secondary" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {isSingle ? 'Single-use Link' : 'Multi-use Link'}
      </Text>
      <div style={{ display: 'flex', gap: '4px', marginTop: '4px', marginBottom: isSingle ? '8px' : '12px' }}>
        <Input value={displayUrl} readOnly size="small" style={{ fontSize: '11px', background: '#f9f9f9' }} />
        <Button size="small" icon={<CopyOutlined />} onClick={() => copyToClipboard(displayUrl)} />
        <Button size="small" icon={<ExportOutlined />} onClick={() => window.open(displayUrl, '_blank')} />
      </div>

      {isSingle && (
        <div style={{ marginBottom: '12px' }}>
          <Text strong style={{ fontSize: '11px' }}>Preview UID:</Text>
          <Input 
            size="small"
            placeholder="e.g. ABC-123" 
            value={uid} 
            onChange={e => setUid(e.target.value)}
            style={{ marginTop: '2px', width: '100%' }}
          />
        </div>
      )}

      <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginBottom: '8px', textAlign: 'center' }}>
        {isSingle ? 'Single-use QR Variations' : 'Multi-use QR Variations'}
      </Text>
      
      <Row gutter={[8, 8]}>
        {[
          { name: 'Black on White', color: '#000000', bgcolor: '#ffffff' },
          { name: 'White on Black', color: '#ffffff', bgcolor: '#000000' },
          { name: 'Smart FG / Trans.', color: fgHex, bgcolor: '#f0f2f5', transparent: true },
          { name: 'Smart FG / Brand BG', color: fgHex, bgcolor: bgHex },
        ].map((opt, index) => {
          // If it's one of the FG variations, use the dynamic label
          const label = opt.name.includes('Smart FG') ? opt.name.replace('Smart FG', fgLabelPrefix) : opt.name;
          
          const keyPrefix = opt.name.includes('Smart FG') ? (hasCustomFgColor ? 'Custom' : 'Smart') : '';
          const keySuffix = opt.name.replace('Smart FG', '').trim();
          const key = `${type}-${keyPrefix}${keySuffix}`;
          const qrSrc = qrImages[key];

          return (
            <Col span={6} key={index}>
              <div style={{ 
                padding: '6px', 
                background: opt.bgcolor, 
                borderRadius: '6px', 
                border: '1px solid #ddd',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'relative',
                minHeight: '80px',
                justifyContent: 'center'
              }}>
                {qrSrc ? (
                  <>
                    <img src={qrSrc} alt={label} style={{ width: '100%', aspectRatio: '1/1', borderRadius: '2px' }} />
                    <Button 
                      size="small" 
                      shape="circle"
                      icon={<DownloadOutlined style={{ fontSize: '9px' }} />} 
                      style={{ 
                        position: 'absolute', 
                        top: '2px', 
                        right: '2px',
                        width: '18px',
                        height: '18px',
                        minWidth: '18px',
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        border: 'none',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onClick={async () => {
                        const downloadUrl = await getQRImage(qrTargetUrl, opt.color, opt.bgcolor, opt.transparent, 1024);
                        downloadQR(downloadUrl, `qr-${id}-${type}-${label.toLowerCase().replace(/ /g, '-')}`);
                      }}
                    />
                  </>
                ) : (
                  <Badge status="processing" />
                )}
              </div>
              <Text style={{ fontSize: '8px', display: 'block', marginTop: '2px', lineHeight: '1', textAlign: 'center' }}>{label}</Text>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export const TemplateLinkPreview = ({ template }) => {
  const [uid, setUid] = useState('');
  const { id, isAutoUidLocked, rgbColor, fgColor, claimType } = template;
  
  const isSingleUseOnly = isAutoUidLocked === true || claimType === 'autoClaim' || claimType === 'thirdPartyAutoClaim';
  const baseUrl = `${payerBaseURL}/tags/${id}`;

  const bgHex = rgbColor ? (rgbColor.startsWith('rgb') ? rgbColorToHex(rgbColor) : rgbColor) : '#ff0098';
  const smartFgColor = fgColor || colorForBackgroundColor(rgbColor || '#ff0098');
  const fgHex = smartFgColor.startsWith('rgb') ? rgbColorToHex(smartFgColor) : (smartFgColor === 'white' ? '#ffffff' : (smartFgColor === 'black' ? '#000000' : smartFgColor));

  const copyToClipboard = useCallback((text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text);
      message.success('Link copied to clipboard');
    } else {
      message.error('Clipboard access not supported');
    }
  }, []);

  const getQRImage = useCallback(async (url, color, bgcolor, transparent = false, size = 1024) => {
    try {
      const canvas = document.createElement('canvas');
      const canvasSize = size;
      canvas.width = canvasSize;
      canvas.height = canvasSize;

      await QRCode.toCanvas(canvas, url, {
        errorCorrectionLevel: 'H', // High error correction to allow for logo
        margin: 2,
        width: canvasSize,
        color: {
          dark: color,
          light: transparent ? '#00000000' : bgcolor,
        }
      });

      const ctx = canvas.getContext('2d');
      const logoImg = new Image();
      logoImg.src = img_qr_logo;

      await new Promise((resolve) => {
        logoImg.onload = resolve;
      });

      // Calculate logo size and position
      const logoSize = canvasSize * 0.22; // Logo is 22% of QR size
      const padding = canvasSize * 0.02; // 2% padding for white border
      const bgSize = logoSize + (padding * 2);
      
      const x = (canvasSize - logoSize) / 2;
      const y = (canvasSize - logoSize) / 2;
      const bgX = (canvasSize - bgSize) / 2;
      const bgY = (canvasSize - bgSize) / 2;

      // Draw white background ("Quiet Zone") for logo to ensure scannability
      ctx.fillStyle = '#ffffff'; 
      ctx.beginPath();
      // Draw a circle for the quiet zone
      const centerX = canvasSize / 2;
      const centerY = canvasSize / 2;
      const radius = bgSize / 2;
      ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
      ctx.fill();

      ctx.drawImage(logoImg, x, y, logoSize, logoSize);

      return canvas.toDataURL('image/png');
    } catch (err) {
      console.error('Failed to generate QR', err);
      return '';
    }
  }, []);

  const [qrImages, setQrImages] = useState({});

  useEffect(() => {
    const generateAll = async () => {
      const options = [
        { name: 'Black on White', color: '#000000', bgcolor: '#ffffff' },
        { name: 'White on Black', color: '#ffffff', bgcolor: '#000000' },
        { name: 'Smart FG / Trans.', color: fgHex, bgcolor: '#f0f2f5', transparent: true },
        { name: 'Smart FG / Brand BG', color: fgHex, bgcolor: bgHex },
      ];

      const newQrImages = {};
      const tagId = id;
      
      const multiUrl = `${baseUrl}`;
      const singleUrl = `${baseUrl}?uid=${uid || 'TEST'}`;

      for (const opt of options) {
        const keyPrefix = opt.name.includes('Smart FG') ? (fgColor ? 'Custom' : 'Smart') : '';
        const keySuffix = opt.name.replace('Smart FG', '').trim();
        const key = `${keyPrefix}${keySuffix}`;
        
        // Multi
        const multiKey = `multi-${key}`;
        newQrImages[multiKey] = await getQRImage(multiUrl, opt.color, opt.bgcolor, opt.transparent, 256);
        
        // Single
        const singleKey = `single-${key}`;
        newQrImages[singleKey] = await getQRImage(singleUrl, opt.color, opt.bgcolor, opt.transparent, 256);
      }
      setQrImages(newQrImages);
    };

    generateAll();
  }, [baseUrl, uid, fgHex, bgHex, id, fgColor, getQRImage]);

  const downloadQR = useCallback(async (url, name) => {
    // For download, we want a higher resolution QR
    // Since url is already a dataURL from getQRImage in the onClick handler
    const link = document.createElement('a');
    link.href = url;
    link.download = `${name}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  const sectionProps = {
    baseUrl,
    uid,
    setUid,
    fgHex,
    bgHex,
    id,
    copyToClipboard,
    getQRImage,
    downloadQR,
    qrImages,
    hasCustomFgColor: !!fgColor
  };

  return (
    <Card size="small" style={{ borderRadius: '12px', textAlign: 'center' }}>
      <Title level={5} style={{ marginBottom: '16px' }}>Onboarding Link & QR</Title>
      
      {!isSingleUseOnly && (
        <>
          <LinkAndQRSection type="multi" {...sectionProps} />
          <Divider style={{ margin: '20px 0' }} />
        </>
      )}
      
      <LinkAndQRSection type="single" {...sectionProps} />
    </Card>
  );
};

export const TemplatePaymentPreview = ({ template, isAdmin }) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [loadingPartner, partner] = useClient(template.partnerClientId);

  const previewMember = useMemo(() => {
    const members = [
      { name: 'Mary D.', image: img_mary },
      { name: 'Jim W.', image: img_jim }
    ];
    return members[Math.floor(Math.random() * members.length)];
  }, [template.id]);

  const {
    rgbColor,
    values = [],
    percents = [],
    selectedValueByIndex = 0,
    paymentDetails = {},
    displayName,
    avatar,
    product = 'tipping',
    actionText: templateActionText
  } = template;

  const topBackgoundColor = rgbColor || '#ff0098';
  const baseContrastColor = colorForBackgroundColor(topBackgoundColor);
  
  // Selection color logic from strike-payer: if brand color is too close to black, 
  // use magenta (#ff0098) as the selection color.
  const selectionColor = generateSelectedColor(topBackgoundColor) || '#ff0098';
  const selectionContrastColor = colorForBackgroundColor(selectionColor);

  const currency = paymentDetails.currency || 'EUR';

  // Selections logic from strike-payer
  const hasPercents = percents && percents.length > 0;
  const items = hasPercents ? percents : (values.length > 0 ? values : [400, 500, 800, 1000]);
  const safeIndex = (selectedValueByIndex >= 0 && selectedValueByIndex < items.length) ? selectedValueByIndex : 0;
  const selectedValue = items[safeIndex] || 0;

  // Fee options logic from strike-payer
  const includeFeesByDefault = paymentDetails.shouldAddOnFees ?? true;
  const allowFeesToggle = paymentDetails.shouldShowFeesOption ?? true;

  const getActionLabel = () => {
    switch (product) {
      case 'tipping': return 'Leave a tip for';
      case 'payment': return 'Make a payment to';
      case 'giving': return 'Donate to';
      default: return 'Pay to';
    }
  };

  const actionLabel = getActionLabel();

  const getCurrencySymbol = (ccy) => {
    switch(ccy) {
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'USD': return '$';
      default: return '€';
    }
  };

  const displayItems = items;
  const colSpan = 8; // 3 columns per row for mobile-style preview

  const billAmount = 0; // €0.00 base for calculation
  const calculatedValue = hasPercents ? Math.round(billAmount * (selectedValue / 10000)) : selectedValue;

  return (
    <Card size="small" bodyStyle={{ padding: 0 }} style={{ maxWidth: '400px', margin: 'auto', borderRadius: '12px', overflow: 'hidden', position: 'relative', background: '#000', border: '1px solid #333' }}>
      <Button 
        type="text" 
        icon={<SettingOutlined style={{ color: baseContrastColor }} />} 
        style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 10, color: baseContrastColor, fontWeight: '500' }}
        onClick={() => setShowEditModal(true)}
      >
        <span style={{ color: baseContrastColor }}>Edit</span>
      </Button>
      
      <div style={{ background: topBackgoundColor, padding: '40px 20px 20px', textAlign: 'center', color: baseContrastColor }}>
        <div style={{ marginBottom: '12px' }}>
          <img 
            src={previewMember.image} 
            alt="avatar" 
            style={{ width: '64px', height: '64px', borderRadius: '50%', border: `2px solid ${baseContrastColor}`, objectFit: 'cover' }} 
          />
        </div>
        <div style={{ fontSize: '13px', opacity: 0.8 }}>{actionLabel}</div>
        <Title level={4} style={{ color: baseContrastColor, margin: '2px 0', fontSize: '18px' }}>{previewMember.name}</Title>
        <div style={{ fontSize: '11px', opacity: 0.8 }}>(Team Name)</div>
        {template.partnerClientId && (
          <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <Avatar 
              size={24} 
              shape="square" 
              src={partner?.avatar} 
              icon={<UserOutlined />} 
              style={{ borderRadius: '4px' }}
            />
            <span style={{ fontSize: '13px', fontWeight: '500' }}>{partner?.displayName || 'Member Name'}</span>
          </div>
        )}
      </div>

      <div style={{ padding: '16px', background: '#000' }}>
        {hasPercents && (
          <div style={{ marginBottom: '16px', textAlign: 'left' }}>
            <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', marginBottom: '8px' }}>
              Bill amount
            </div>
            <div style={{ 
              background: '#141414', 
              border: '1px solid #fff', 
              borderRadius: '8px', 
              padding: '12px',
              textAlign: 'center',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '64px'
            }}>
              <span style={{ fontSize: '36px', color: 'rgba(255,255,255,0.4)', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                <span style={{ fontSize: '24px', marginRight: '2px', marginTop: '4px' }}>{getCurrencySymbol(currency)}</span>
                0
              </span>
              <div style={{ 
                position: 'absolute', 
                height: '32px', 
                width: '1px', 
                background: topBackgoundColor, 
                left: '55%' // Approximate cursor position
              }} />
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
          {displayItems.map((val, idx) => (
            <div key={idx} style={{ flex: 1, minWidth: 0 }}>
              <Button 
                block 
                style={{ 
                  height: '44px', 
                  background: idx === safeIndex ? selectionColor : '#1f1f1f',
                  borderColor: idx === safeIndex ? selectionColor : '#333',
                  color: idx === safeIndex ? selectionContrastColor : '#fff',
                  fontWeight: 'bold',
                  borderRadius: '4px',
                  fontSize: displayItems.length > 3 ? '11px' : '13px',
                  padding: 0,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap'
                }}
              >
                {hasPercents ? `${(val / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%` : <MoneyTitle amount={val} currency={currency} digits={0} />}
              </Button>
            </div>
          ))}
        </div>
        
        <div style={{ textAlign: 'center', margin: '12px 0', color: '#fff', fontSize: '13px', cursor: 'pointer' }}>
          Custom amount <EditOutlined style={{ fontSize: '11px' }} />
        </div>

        {/* Dynamic Fields from Payment Details */}
        <div style={{ marginTop: '20px' }}>
          {(paymentDetails.fieldSettings || []).map((field, idx) => {
            const type = field.type;
            const requirement = field.requirement;
            
            if (requirement === 'hidden') return null;

            const isName = type === 'name';
            const isMobile = type === 'mobile';
            const isMessage = type === 'message';
            const isReview = type === 'review';

            const label = isName ? 'Your name' : 
                        (isMobile ? 'Phone number' : 
                        (isReview ? 'Comment' : 
                        (isMessage ? 'Message' : (field.label || 'Field'))));
            
            const placeholder = isName ? 'Enter your name (private)' :
                              (isMobile ? '+353 (87) 123-4567' :
                              (isReview ? 'Share a comment' :
                              (isMessage ? 'Send a private message' : `Enter ${label.toLowerCase()}`)));

            const isRequired = requirement === 'required';

            return (
              <div key={idx} style={{ marginBottom: '16px', textAlign: 'left' }}>
                <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', marginBottom: '8px' }}>
                  {label} <span style={{ opacity: 0.45, fontSize: '12px' }}>({isRequired ? 'required' : 'optional'})</span>
                </div>
                <div style={{ 
                  background: '#141414', 
                  border: isMobile ? '1px solid #fff' : '1px solid #333', 
                  borderRadius: '8px', 
                  padding: '10px 12px',
                  color: isMobile ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.3)',
                  fontSize: '14px',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {isMobile && (
                    <div style={{ 
                      height: '20px', 
                      width: '1px', 
                      background: selectionColor, 
                      marginRight: '1px'
                    }} />
                  )}
                  {placeholder}
                </div>
              </div>
            );
          })}
        </div>

        {allowFeesToggle && (
          <div style={{ background: '#141414', padding: '10px', borderRadius: '8px', marginBottom: '12px', border: '1px solid #333' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ color: '#fff', fontSize: '13px' }}>Include fees in {product === 'tipping' ? 'tip' : 'payment'}</div>
                <div style={{ color: '#888', fontSize: '11px' }}>
                  <MoneyTitle amount={Math.round(calculatedValue * 0.075)} currency={currency} />
                </div>
              </div>
              <Switch checked={includeFeesByDefault} size="small" />
            </div>
          </div>
        )}

        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <div style={{ color: '#888', fontSize: '11px' }}>Total to pay</div>
          <div style={{ color: '#fff', fontSize: '20px', fontWeight: 'bold' }}>
            <MoneyTitle amount={calculatedValue + (includeFeesByDefault ? Math.round(calculatedValue * 0.075) : 0)} currency={currency} />
          </div>
        </div>

        <Button 
          block 
          type="primary" 
          shape="round" 
          size="large" 
          style={{ 
            height: '44px', 
            background: '#fff', 
            borderColor: '#fff', 
            color: '#000', 
            fontWeight: '500', 
            fontSize: '19px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            pointerEvents: 'none'
          }}
        >
          <AppleFilled style={{ fontSize: '22px', marginBottom: '2px' }} /> Pay
        </Button>

        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <img 
            src={product === 'tipping' ? img_striketip_logo : img_strikepay_logo} 
            alt="logo" 
            style={{ height: '18px', filter: 'brightness(0) invert(1)' }} 
          />
        </div>
        
        <div style={{ textAlign: 'center', marginTop: '12px', fontSize: '9px', color: '#555' }}>
          <a style={{ color: 'inherit', textDecoration: 'underline' }}>Terms & Conditions</a> - <a style={{ color: 'inherit', textDecoration: 'underline' }}>Privacy Policy</a>
          <div style={{ marginTop: '2px' }}>© 2026 Strike Technology Ltd</div>
          <div style={{ marginTop: '2px', opacity: 0.5 }}>{template.id}</div>
        </div>
      </div>

      {showEditModal && (
        <TemplateTipEditorModal 
          visible={showEditModal} 
          onCancel={() => setShowEditModal(false)} 
          template={template} 
          isAdmin={isAdmin}
        />
      )}
    </Card>
  );
};

const webhookStatuses = [
  { label: 'Pending', value: 'pending' },
  { label: 'Success', value: 'success' },
  { label: 'Failed', value: 'failed' },
  { label: 'Show All Statuses', value: 'all' },
];

const webhookModes = [
  { label: 'Live', value: false },
  { label: 'Test', value: true },
  { label: 'Show All Modes', value: 'all' },
];

const ALL_WEBHOOK_EVENTS = [
  "balance_changed",
  "client_created",
  "client_email_verified",
  "client_live",
  "client_kyc_started",
  "client_kyc_verified",
  "client_kyc_failed",
  "client_kyc_expired",
  "client_paused",
  "client_unpaused",
  "delegate_invited",
  "invoice_charged",
  "member_joined",
  "member_removed",
  "order_confirmed",
  "payment_available",
  "payment_pending",
  "payment_success",
  "refund_issued",
  "tag_claimed",
  "tag_paused",
  "tag_unpaused",
  "transfer_failed",
  "transfer_success",
  "withdrawal_failed",
  "withdrawal_paid",
  "withdrawal_refunded",
  "withdrawal_sent",
  "team_site_created",
  "team_site_activated",
  "team_site_updated",
  "team_site_deactivated",
  "team_site_clicked",
];

const WEBHOOK_EVENT_SET = new Set(ALL_WEBHOOK_EVENTS);
const EVENT_FAMILY_ORDER = ['client', 'tag', 'payment', 'transfer', 'withdrawal', 'invoice', 'order', 'delegate', 'member', 'balance', 'other'];

const getEventFamily = (event) => {
  if (!event || typeof event !== 'string') return 'other';
  const separatorIndex = event.indexOf('_');
  if (separatorIndex <= 0) return 'other';
  return event.substring(0, separatorIndex);
};

const buildWebhookEventGroups = (events = []) => {
  const groupedEvents = events.reduce((acc, event) => {
    const family = getEventFamily(event);
    if (!acc[family]) acc[family] = [];
    acc[family].push(event);
    return acc;
  }, {});

  return Object.keys(groupedEvents)
    .sort((a, b) => {
      const indexA = EVENT_FAMILY_ORDER.indexOf(a);
      const indexB = EVENT_FAMILY_ORDER.indexOf(b);
      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    })
    .map((family) => ({
      family,
      events: Array.from(new Set(groupedEvents[family])).sort(),
    }));
};

const tagColorForEvent = (type) => {
  switch (type) {
    case 'tag_claimed':
      return 'blue';
    case 'payment_success':
    case 'payment_available':
    case 'transfer_success':
    case 'transfer_successful':
    case 'tag_unpaused':
    case 'client_unpaused':
    case 'withdrawal_paid':
      return 'green';
    case 'payment_pending':
    case 'withdrawal_sent':
      return 'gold';
    case 'transfer_failed':
    case 'withdrawal_failed':
    case 'withdrawal_refunded':
      return 'red';
    case 'tag_paused':
    case 'client_paused':
      return 'volcano';
    case 'order_confirmed':
    case 'invoice_charged':
      return 'geekblue';
    case 'delegate_invited':
    case 'refund_issued':
      return 'magenta';
    case 'member_joined':
      return 'lime';
    case 'member_removed':
      return 'volcano';
    case 'team_site_created':
      return 'cyan';
    case 'team_site_activated':
      return 'green';
    case 'team_site_updated':
      return 'blue';
    case 'team_site_deactivated':
      return 'volcano';
    case 'team_site_clicked':
      return 'cyan';
    case 'client_created':
      return 'cyan';
    case 'client_email_verified':
      return 'blue';
    case 'client_live':
      return 'green';
    case 'client_kyc_started':
      return 'gold';
    case 'client_kyc_failed':
      return 'red';
    case 'client_kyc_verified':
    case 'client_kyc_expired':
    case 'balance_changed':
      return 'purple';
    default:
      return 'default';
  }
};

export const WebhookStatusSelector = ({ value, onChange }) => {
  const onChangeSelection = (selectedStatuses) => {
    const lastSelected = selectedStatuses[selectedStatuses.length - 1];
    
    if (lastSelected === 'all' || selectedStatuses.length === 0) {
      // If "Show All" was clicked, or everything cleared, reset to All
      onChange(undefined);
    } else {
      // If a specific status was clicked, remove "all" from the selection
      const filtered = selectedStatuses.filter(s => s !== 'all');
      onChange(filtered.length > 0 ? filtered : undefined);
    }
  }
  
  let displayValue = ['all'];
  if (value !== undefined) {
    displayValue = Array.isArray(value) ? value : [value];
  }

  return (
    <Select 
      mode="multiple"
      value={displayValue} 
      onChange={onChangeSelection} 
      style={{ minWidth: '180px' }}
      placeholder="Filter by Status"
    >
      {webhookStatuses.map((status) => (
        <Select.Option key={status.value} value={status.value}>{status.label}</Select.Option>
      ))}
    </Select>
  );
}

export const WebhookModeSelector = ({ value, onChange }) => {
  const onChangeSelection = (selectedMode) => {
    onChange(selectedMode === 'all' ? undefined : selectedMode);
  }
  return (
    <Select value={value === undefined ? 'all' : value} onChange={onChangeSelection} style={{ minWidth: '140px' }}>
      {webhookModes.map((mode) => (
        <Select.Option key={String(mode.value)} value={mode.value}>{mode.label}</Select.Option>
      ))}
    </Select>
  );
}

// This is a multi-selector with an array return
export const WebhookTypesSelector = ({ value, onChange, availableTypes }) => {
  const webhookTypeValues = availableTypes || ALL_WEBHOOK_EVENTS;
  const groupedWebhookTypes = buildWebhookEventGroups(webhookTypeValues);

  const onChangeSelection = (selectedTypes) => {
    const selectedTypesArray = selectedTypes.length > 0 ? selectedTypes : undefined;
    onChange(selectedTypesArray);
  }

  return (
    <Select 
      mode="multiple" 
      placeholder="Filter by Event Types"
      style={{ minWidth: '200px', maxWidth: '800px' }} 
      value={value} 
      onChange={onChangeSelection}
    >
      {groupedWebhookTypes.map((group) => (
        <Select.OptGroup key={group.family} label={group.family}>
          {group.events.map((event) => (
            <Select.Option key={event} value={event}>
              <Tag color={tagColorForEvent(event)}>{event}</Tag>
            </Select.Option>
          ))}
        </Select.OptGroup>
      ))}
    </Select>
  );
}

export const WebhookAppSelector = ({ configs, value, onChange }) => {
  const onChangeSelection = (selectedConfigId) => {
    onChange(selectedConfigId === 'all' ? undefined : selectedConfigId);
  }
  return (
    <Select 
      placeholder="Select Application"
      style={{ minWidth: '200px' }} 
      value={value || 'all'} 
      onChange={onChangeSelection}
    >
      <Select.Option value="all">All Applications</Select.Option>
      {configs && configs.map((config) => (
        <Select.Option key={config.id} value={config.id}>
          {config.description ? `${config.description} (${config.id.substring(0, 8)})` : config.id}
        </Select.Option>
      ))}
    </Select>
  );
}

const ClientInfo = ({ clientId, href }) => {
  const [loading, client] = useClient(clientId);
  if (loading) return <span>...</span>;
  if (!client) return <span>Unknown</span>;
  
  const content = (
    <Space size="small" align="start">
      <Avatar size="small" src={client.avatar} icon={<UserOutlined />} />
      <Space direction="vertical" size={0}>
        <Text strong style={{ fontSize: '13px', lineHeight: '1.2' }}>{client.displayName || client.id}</Text>
        <Text type="secondary" style={{ fontSize: '11px', lineHeight: '1.2' }}>{client.email || 'No email'}</Text>
      </Space>
    </Space>
  );

  if (href) {
    return <a href={href} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>{content}</a>;
  }
  return content;
};

/** The template's current hero as the editor shows it: the sign-up image,
 * else the legacy `avatar` where this editor used to save the upload. Same
 * two-field rule as every payer hero surface. */
export const templateHeroUrl = (template) => (
  (template && template.actionText && template.actionText.signupImage)
  || (template && template.avatar)
  || null
);

/** What a hero upload WRITES: actionText.signupImage only. `avatar` is left
 * exactly as it was — the whole template is spread onto every minted tag,
 * and a tag's avatar doubles as the tip page's profile picture when a team
 * has no logo (strike-payer taps-helper), so the hero must never land there
 * again (Charles, 2026-10-09: production s710eu carried its hero on avatar
 * and the Issuers editor's signupImage silently out-ranked a Partners
 * re-upload). */
export const templateHeroUpdate = (template, uploadedUrl) => ({
  avatar: (template && template.avatar) || null,
  signupImage: uploadedUrl
    || (template && template.actionText && template.actionText.signupImage)
    || null,
});

const TemplateEditorModal = ({ visible, onCancel, template, isAdmin }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [heroUrl, setHeroUrl] = useState(templateHeroUrl(template));
  const [uploadedHeroUrl, setUploadedHeroUrl] = useState(null);

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({
        heading: template.actionText?.heading || '',
        subheading: template.actionText?.subheading || '',
        claim: template.actionText?.claim || '',
        claimTerm: template.claimTerm || '',
        postClaimTitle: template.actionText?.postClaimTitle || '',
        postClaimText: template.actionText?.postClaimText || '',
        postClaimLink: template.actionText?.postClaimLink || '',
        postClaimLinkText: template.actionText?.postClaimLinkText || '',
        questionsTitle: template.actionText?.questionsTitle || '',
        questions: template.actionText?.questions || [],
        rgbColor: template.rgbColor || '',
        fgColor: template.fgColor || null,
        claimType: template.claimType || null,
        isAutoUidLocked: template.isAutoUidLocked ? 'single' : 'multi',
        limitCount: template.limitCount || null,
        clientId: template.clientId || null,
        setupRequired: !!template.setupRequired,
        isNotBillable: !!template.isNotBillable,
        isTeamTag: template.isTeamTag === true ? 'true' : template.isTeamTag === false ? 'false' : '',
        deviceKind: template.deviceKind || '',
        pdCurrency: (template.paymentDetails && template.paymentDetails.currency) || '',
        pdProcessor: (template.paymentDetails && template.paymentDetails.processor) || '',
        isMakeDefaultTag: template.isMakeDefaultTag === true ? 'true' : template.isMakeDefaultTag === false ? 'false' : '',
        isDefaultTag: template.isDefaultTag === true ? 'true' : template.isDefaultTag === false ? 'false' : '',
        product: template.product,
      });
      setHeroUrl(templateHeroUrl(template));
      setUploadedHeroUrl(null);
    }
  }, [visible, template, form]);

  const onFinish = async (formValues) => {
    setLoading(true);
    try {
      const model = new StrikeTemplateTagModel({ 
        issuerId: template.issuerId || 'strike', 
        templateTagId: template.id 
      });

      const values = { ...form.getFieldsValue(true), ...formValues };

      const currentActionText = template.actionText || {};
      const hero = templateHeroUpdate(template, uploadedHeroUrl);
      const newActionText = {
        ...currentActionText,
        signupImage: hero.signupImage,
        heading: values.heading !== undefined ? (values.heading || null) : currentActionText.heading,
        subheading: values.subheading !== undefined ? (values.subheading || null) : currentActionText.subheading,
        claim: values.claim !== undefined ? (values.claim || null) : currentActionText.claim,
        postClaimTitle: values.postClaimTitle !== undefined ? (values.postClaimTitle || null) : currentActionText.postClaimTitle,
        postClaimText: values.postClaimText !== undefined ? (values.postClaimText || null) : currentActionText.postClaimText,
        postClaimLink: values.postClaimLink !== undefined ? (values.postClaimLink || null) : currentActionText.postClaimLink,
        postClaimLinkText: values.postClaimLinkText !== undefined ? (values.postClaimLinkText || null) : currentActionText.postClaimLinkText,
        questionsTitle: values.questionsTitle !== undefined ? (values.questionsTitle || null) : currentActionText.questionsTitle,
        questions: values.questions !== undefined ? 
          (values.questions || []).map(q => ({
            question: q.question || '',
            answer: q.answer || '',
          })) : currentActionText.questions,
      };

      const updatedData = {
        partnerClientId: template.partnerClientId || null, 
        rgbColor: values.rgbColor !== undefined ? (values.rgbColor || '') : template.rgbColor,
        fgColor: values.fgColor !== undefined ? (values.fgColor || null) : template.fgColor,
        claimTerm: values.claimTerm !== undefined ? (values.claimTerm?.trim() || null) : template.claimTerm,
        claimType: values.claimType !== undefined ? values.claimType : (template.claimType || null),
        isAutoUidLocked: values.isAutoUidLocked !== undefined ? (values.isAutoUidLocked === 'single') : !!template.isAutoUidLocked,
        limitCount: values.limitCount !== undefined ? (values.limitCount || null) : (template.limitCount || null),
        avatar: hero.avatar,
        actionText: newActionText,
        clientId: values.clientId !== undefined ? (values.clientId || null) : (template.clientId || null),
        // Tri-state matters: null = "not a setup template" (payer shows no
        // setup affordance at all); false = "setup completed" (payer shows the
        // discreet change link). Unchecking the toggle means null, never false.
        setupRequired: values.setupRequired !== undefined
          ? (values.setupRequired ? true : null)
          : (template.setupRequired ?? null),
        isNotBillable: values.isNotBillable !== undefined ? !!values.isNotBillable : !!template.isNotBillable,
        isTeamTag: values.isTeamTag !== undefined
          ? (values.isTeamTag === 'true' ? true : values.isTeamTag === 'false' ? false : null)
          : template.isTeamTag,
        deviceKind: values.deviceKind !== undefined
          ? (values.deviceKind || null)
          : (template.deviceKind || null),
        // Sub-map writes are where fields get lost: never write a partial map and
        // trust merge semantics. Read-spread-write the WHOLE map (same pattern as
        // actionText above), so every existing key is explicitly carried forward.
        // Cleared selects keep the old value rather than writing null — a null
        // currency would propagate to claimed tags and break payment routing.
        ...(values.pdCurrency !== undefined || values.pdProcessor !== undefined ? {
          paymentDetails: {
            ...(template.paymentDetails || {}),
            ...(values.pdCurrency ? { currency: values.pdCurrency } : {}),
            ...(values.pdProcessor ? { processor: values.pdProcessor } : {}),
          },
        } : {}),
        isMakeDefaultTag: values.isMakeDefaultTag !== undefined
          ? (values.isMakeDefaultTag === 'true' ? true : values.isMakeDefaultTag === 'false' ? false : null)
          : template.isMakeDefaultTag,
        isDefaultTag: values.isDefaultTag !== undefined
          ? (values.isDefaultTag === 'true' ? true : values.isDefaultTag === 'false' ? false : null)
          : template.isDefaultTag,
      };

      await model.updateTagWithData(updatedData);
      message.success('Template branding updated successfully');
      onCancel();
    } catch (error) {
      console.error('Update template error:', error);
      message.error('Failed to update template');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={`Edit Template Branding: ${template.id}`}
      visible={visible}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={700}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item name="product" hidden />
        <Tabs defaultActiveKey="1" type="card" size="small">
          <TabPane tab={<span><ContactsOutlined /> Branding</span>} key="1">
            <div style={{ padding: '16px 0' }}>
              <Card size="small" title="Banner Background" style={{ marginBottom: '16px', borderRadius: '8px' }}>
                <ColorPickerControlFormItem 
                  label="Background Color"
                  title="Choose a brand color for the template banner" 
                  rgbColor={template.rgbColor} 
                />
              </Card>

              <Card size="small" title="Banner Image" style={{ marginBottom: '16px', borderRadius: '8px' }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: '12px', fontSize: '12px' }}>
                  This image appears in the header of the onboarding page.
                </Text>
                <Form.Item 
                  noStyle 
                  shouldUpdate={(prev, curr) => 
                    prev.rgbColor !== curr.rgbColor || 
                    prev.fgColor !== curr.fgColor || 
                    prev.heading !== curr.heading
                  }
                >
                  {({ getFieldValue }) => {
                    const bgColor = getFieldValue('rgbColor') || '#ff0098';
                    const fgColorOverride = getFieldValue('fgColor');
                    const product = getFieldValue('product') || template.product;
                    const productSwitcher = new ProductSwitcher({});
                    const productMap = productSwitcher.getProductMap(product);
                    const heading = getFieldValue('heading') || productMap.heading;
                    const subheading = getFieldValue('subheading') || productMap.subheading;
                    const contrastColor = (fgColorOverride && fgColorOverride !== 'null') ? fgColorOverride : colorForBackgroundColor(bgColor);
                    
                    return (
                      <div style={{ 
                        backgroundColor: bgColor, 
                        borderRadius: '8px', 
                        marginBottom: '12px',
                        textAlign: 'left',
                        transition: 'background-color 0.3s ease',
                        overflow: 'hidden'
                      }}>
                        <div style={{ padding: '24px 24px 0 24px' }}>
                          <Title level={4} style={{ color: contrastColor, margin: 0, fontSize: '20px' }}>{heading}</Title>
                          <Text style={{ color: contrastColor, opacity: 0.9, fontSize: '14px', display: 'block', marginBottom: '16px' }}>{subheading}</Text>
                        </div>
                        <div style={{ width: '100%', margin: '20px 0' }}>
                          <TemplateTagImageEditor 
                            templateId={template.id} 
                            issuerId={template.issuerId}
                            currentUrl={heroUrl}
                            onUpdateImage={(url) => { setUploadedHeroUrl(url); setHeroUrl(url); }}
                            isLoggedInMemberAdmin={true}
                          />
                        </div>
                        <div style={{ height: '24px' }} />
                      </div>
                    );
                  }}
                </Form.Item>
              </Card>

              <Card size="small" title="QR & Text Color" style={{ borderRadius: '8px' }}>
                <ColorPickerControlFormItem 
                  label="Foreground Color"
                  title="Choose a color for QR codes and text overrides" 
                  name="fgColor"
                  rgbColor={template.fgColor} 
                />
              </Card>
            </div>
          </TabPane>

          <TabPane tab={<span><EditOutlined /> Content</span>} key="2">
            <div style={{ padding: '16px 0' }}>
              <Card size="small" title="Headings" style={{ marginBottom: '16px', borderRadius: '8px' }}>
                <Form.Item name="heading" label="Main Heading">
                  <Input placeholder="Main heading text" />
                </Form.Item>
                <Form.Item name="subheading" label="Subheading">
                  <Input placeholder="Subheading text" />
                </Form.Item>
              </Card>

              <Card size="small" title="Action Button" style={{ marginBottom: '16px', borderRadius: '8px' }}>
                <Form.Item name="claim" label="Button Text">
                  <Input placeholder="e.g. Sign up now" />
                </Form.Item>
              </Card>

              <Card size="small" title="Claiming" style={{ marginBottom: '16px', borderRadius: '8px' }}>
                <Form.Item
                  name="claimTerm"
                  label="Device name (claimTerm)"
                  extra="e.g. QR Code is the default"
                >
                  <Input placeholder="e.g. QR Code" />
                </Form.Item>
              </Card>

              <Card size="small" title="Post-claim reminder" style={{ borderRadius: '8px' }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: '12px', fontSize: '12px' }}>
                  Shown on the success screen below &quot;What to do next?&quot; to remind people of what&apos;s important.
                </Text>
                <Form.Item name="postClaimTitle" label="Title">
                  <Input placeholder="e.g. Important reminder" />
                </Form.Item>
                <Form.Item name="postClaimText" label="Reminder text">
                  <Input.TextArea rows={3} placeholder="e.g. Remember to display your QR code where customers can see it." />
                </Form.Item>
                <Form.Item name="postClaimLink" label="More info URL">
                  <Input placeholder="https://..." />
                </Form.Item>
                <Form.Item
                  name="postClaimLinkText"
                  label="Link button text"
                  extra="Defaults to &quot;More info...&quot; when empty"
                >
                  <Input placeholder="More info..." />
                </Form.Item>
              </Card>
            </div>
          </TabPane>

          <TabPane tab={<span><QuestionCircleOutlined /> Questions</span>} key="3">
            <div style={{ padding: '16px 0' }}>
              <Card size="small" title="FAQ Section" style={{ borderRadius: '8px' }}>
                <Form.Item name="questionsTitle" label="Section Title">
                  <Input placeholder="Title for the FAQ section (e.g. How it works)" />
                </Form.Item>
                <Divider style={{ margin: '12px 0' }} />
                <Form.List name="questions">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map((field, index) => (
                        <Card 
                          key={field.key} 
                          size="small" 
                          style={{ marginBottom: '12px', background: '#f9f9f9', borderRadius: '8px', border: '1px solid #eee' }}
                          title={`Question ${index + 1}`}
                          extra={
                            <DeleteOutlined 
                              onClick={() => remove(field.name)} 
                              style={{ color: 'red', cursor: 'pointer' }} 
                            />
                          }
                        >
                          <Form.Item
                            {...field}
                            name={[field.name, 'question']}
                            fieldKey={[field.fieldKey, 'question']}
                            label="Question"
                            style={{ marginBottom: '8px' }}
                          >
                            <Input placeholder="Question text" />
                          </Form.Item>
                          <Form.Item
                            {...field}
                            name={[field.name, 'answer']}
                            fieldKey={[field.fieldKey, 'answer']}
                            label="Answer"
                            style={{ marginBottom: 0 }}
                          >
                            <Input.TextArea placeholder="Answer text" autoSize={{ minRows: 2 }} />
                          </Form.Item>
                        </Card>
                      ))}
                      <Form.Item style={{ marginBottom: 0 }}>
                        <Button type="dashed" onClick={() => add({ question: '', answer: '' })} block icon={<PlusOutlined />}>
                          Add Question
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>
              </Card>
            </div>
          </TabPane>

          <TabPane tab={<span><SettingOutlined /> Settings</span>} key="4">
            <div style={{ padding: '16px 0' }}>
              <Card size="small" title="Link Configuration" style={{ marginBottom: '16px', borderRadius: '8px' }}>
                <Form.Item 
                  noStyle
                  shouldUpdate={(prevValues, currentValues) => prevValues.claimType !== currentValues.claimType}
                >
                  {({ getFieldValue, setFieldsValue }) => {
                    const claimType = getFieldValue('claimType');
                    const isAutoClaim = claimType === 'autoClaim' || claimType === 'thirdPartyAutoClaim';
                    
                    // Enforce single-use for auto-claim types
                    if (isAutoClaim && getFieldValue('isAutoUidLocked') !== 'single') {
                      setTimeout(() => setFieldsValue({ isAutoUidLocked: 'single' }), 0);
                    }

                    return (
                      <Form.Item 
                        name="isAutoUidLocked" 
                        label="Link Type" 
                        tooltip={isAutoClaim ? "Auto-claim requires unique IDs (Single-use only)" : "Determines if tags are single-use or multi-use"}
                      >
                        <Select disabled={isAutoClaim}>
                          <Select.Option value="multi">Multi-use links</Select.Option>
                          <Select.Option value="single">Single-use links only</Select.Option>
                        </Select>
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Card>

              <Card size="small" title="Restrictions" style={{ borderRadius: '8px' }}>
                <Form.Item name="limitCount" label="Claim Limit" tooltip="Maximum number of activations per client">
                  <InputNumber style={{ width: '100%' }} min={1} placeholder="Unlimited" />
                </Form.Item>
                <Form.Item 
                  noStyle
                  shouldUpdate={(prevValues, currentValues) => prevValues.claimType !== currentValues.claimType}
                >
                  {({ getFieldValue }) => {
                    const claimType = getFieldValue('claimType');
                    if (claimType === 'thirdPartyAutoClaim' || claimType === 'redirect') {
                      return (
                        <>
                          <Divider style={{ margin: '12px 0' }} />
                          <Form.Item name="setupRequired" label="Setup Required" valuePropName="checked" tooltip="Force users to complete a setup flow after claiming">
                            <Switch />
                          </Form.Item>
                        </>
                      );
                    }
                    return null;
                  }}
                </Form.Item>
              </Card>
            </div>
          </TabPane>

          {isAdmin && (
            <TabPane tab={<span><SafetyCertificateOutlined /> Admin</span>} key="5">
              <div style={{ padding: '16px 0' }}>
                <Card 
                  size="small" 
                  title="Internal Settings" 
                  style={{ borderRadius: '8px', border: '1px solid #ffd591', background: '#fffbe6' }}
                >
                  <Form.Item name="claimType" label="Claim Type">
                    <Select allowClear placeholder="Default (isAutoTag)">
                      <Select.Option value="autoClaim">Auto Claim</Select.Option>
                      <Select.Option value="thirdPartyAutoClaim">3rd Party Auto Claim</Select.Option>
                      <Select.Option value="redirect">Redirect</Select.Option>
                    </Select>
                  </Form.Item>

                  <Form.Item 
                    noStyle
                    shouldUpdate={(prevValues, currentValues) => prevValues.claimType !== currentValues.claimType}
                  >
                    {({ getFieldValue }) => {
                      const claimType = getFieldValue('claimType');
                      if (claimType === 'autoClaim' || claimType === 'thirdPartyAutoClaim') {
                        return (
                          <>
                            <Form.Item name="clientId" label="Target Client ID" tooltip="The Client ID this tag will be claimed to">
                              <Input placeholder="Enter Client ID" />
                            </Form.Item>
                            <Form.Item 
                              noStyle
                              shouldUpdate={(prevValues, currentValues) => prevValues.clientId !== currentValues.clientId}
                            >
                              {({ getFieldValue }) => {
                                const cid = getFieldValue('clientId');
                                if (!cid) return null;
                                return (
                                  <div style={{ marginBottom: '16px', padding: '8px', background: '#f5f5f5', borderRadius: '4px', border: '1px solid #d9d9d9' }}>
                                    <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: '4px' }}>CLIENT PREVIEW</Text>
                                    <ClientInfo clientId={cid} />
                                  </div>
                                );
                              }}
                            </Form.Item>
                          </>
                        );
                      }
                      return null;
                    }}
                  </Form.Item>

                  <Divider style={{ margin: '12px 0' }} />

                  <Form.Item name="isNotBillable" label="Is Not Billable" valuePropName="checked" tooltip="Exclude tags created from this template from billing">
                    <Switch />
                  </Form.Item>

                  <Form.Item
                    name="isTeamTag"
                    label="Is Team Tag"
                    tooltip="When true, this template can only be claimed by teams. When false, individual only. When not set, both are allowed."
                  >
                    <Select allowClear placeholder="Not set">
                      <Select.Option value="">Not set</Select.Option>
                      <Select.Option value="true">Team Tag</Select.Option>
                      <Select.Option value="false">Individual Tag</Select.Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="deviceKind"
                    label="Device Kind (Smart POS)"
                    tooltip="Tags claimed from this template are Smart POS hand-off devices. The QR family (stand / sticker / card / fob) behaves identically — armable, bare-scan binding — and differs only in physical form. Terminal: driveable payment terminal (claim with the terminal id as the tag uid, e.g. tmr_… for Stripe). Not set: ordinary tag — never armable."
                  >
                    <Select allowClear placeholder="Not set">
                      <Select.Option value="">Not set</Select.Option>
                      <Select.Option value="stand">Stand (QR on a counter)</Select.Option>
                      <Select.Option value="sticker">Sticker (QR on a window/mirror)</Select.Option>
                      <Select.Option value="card">Card (QR carried by a person)</Select.Option>
                      <Select.Option value="fob">Fob (QR on a keyring)</Select.Option>
                      <Select.Option value="terminal">Terminal (driveable)</Select.Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="pdCurrency"
                    label="Default currency"
                    tooltip="paymentDetails.currency on tags claimed from this template. Drives Stripe account routing (EUR→stripe, USD→stripeus, GBP→stripegb)."
                  >
                    <Select allowClear placeholder="Not set">
                      <Select.Option value="EUR">EUR</Select.Option>
                      <Select.Option value="USD">USD</Select.Option>
                      <Select.Option value="GBP">GBP</Select.Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="pdProcessor"
                    label="Default processor"
                    tooltip="paymentDetails.processor override on claimed tags — beats the per-currency processor table. A terminal serves exactly one processor."
                  >
                    <Select allowClear placeholder="Not set (currency rules decide)">
                      {/* Same vocabulary as Ops > Processors; 'internal' is
                          bookkeeping, never a selectable routing target. */}
                      {PROCESSOR_KEYS.filter((key) => key !== 'internal').map((key) => (
                        <Select.Option key={key} value={key}>{key}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="isMakeDefaultTag"
                    label="Team default tag on claim"
                    tooltip="When true, after a team claims a tag from this template, that claimed tag becomes this team's default (tip button, etc.). Does not change the global default template."
                  >
                    <Select allowClear placeholder="Not set">
                      <Select.Option value="">Not set</Select.Option>
                      <Select.Option value="true">true</Select.Option>
                      <Select.Option value="false">false</Select.Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="isDefaultTag"
                    label="Global default team tipping template"
                    tooltip="Only one template should be global default. New teams use this template when no templateTagId is set. Edit here in Admin — not during claim."
                  >
                    <Select allowClear placeholder="Not set">
                      <Select.Option value="">Not set</Select.Option>
                      <Select.Option value="true">true</Select.Option>
                      <Select.Option value="false">false</Select.Option>
                    </Select>
                  </Form.Item>
                </Card>
              </div>
            </TabPane>
          )}
        </Tabs>
      </Form>
    </Modal>
  );
};

export const TemplateOnboardPreview = ({ template, isAdmin }) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const {
    product,
    topLogo,
    avatar,
    actionText,
    displayName,
    rgbColor,
    fgColor,
  } = template;

  const topBackgoundColor = rgbColor || '#ff0098';
  const baseContrastColor = colorForBackgroundColor(topBackgoundColor);
  const contrastColor = (fgColor && fgColor !== 'null') ? fgColor : baseContrastColor;

  const backgroundColorStyle = { 
    backgroundColor: topBackgoundColor, 
    width: '100%', 
    color: contrastColor,
    textAlign: 'left'
  };

  const productSwitcher = new ProductSwitcher({});
  const productMap = productSwitcher.getProductMap(product);
  let { 
    heading, 
    subheading, 
    confirmClaimButtonText, 
    signupWarningText,
    defaultSignupText 
  } = productMap;

  let additionalInfoForTag = [];

  if (actionText) {
    if (typeof actionText === 'string') {
      heading = actionText;
    } else {
      heading = actionText.heading || heading;
      subheading = actionText.subheading || subheading;
      confirmClaimButtonText = actionText.claim || confirmClaimButtonText;
      defaultSignupText = actionText.signup || defaultSignupText;
      additionalInfoForTag = actionText.questions || additionalInfoForTag;
      signupWarningText = actionText.questionsTitle || signupWarningText;
    }
  }

  // In preview, let's show the signup text as it's the most common first-view
  const displayButtonText = defaultSignupText;

  return (
    <Card size="small" bodyStyle={{ padding: 0 }} style={{ maxWidth: '400px', margin: 'auto', borderRadius: '12px', overflow: 'hidden', position: 'relative' }}>
      <Button 
        type="text" 
        icon={<SettingOutlined style={{ color: baseContrastColor }} />} 
        style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 10, color: baseContrastColor, fontWeight: '500' }}
        onClick={() => setShowEditModal(true)}
      >
        <span style={{ color: baseContrastColor }}>Edit</span>
      </Button>
      
      <div style={backgroundColorStyle}>
        <div style={{ padding: '24px 24px 0 24px' }}>
          <Title level={3} style={{ color: contrastColor, margin: 0 }}>{heading}</Title>
          <Text style={{ color: contrastColor, opacity: 0.9, fontSize: '16px', display: 'block', marginBottom: '16px' }}>{subheading}</Text>
        </div>
        
        <div style={{ margin: '20px 0' }}>
          {(actionText?.signupImage || avatar) ? (
            <img 
              src={actionText?.signupImage || avatar} 
              alt="banner" 
              style={{ width: '100%', maxHeight: '350px', objectFit: 'contain' }} 
            />
          ) : (
            <div style={{ padding: '40px', background: 'rgba(255,255,255,0.1)', textAlign: 'center' }}>
              <TeamOutlined style={{ fontSize: '48px', color: contrastColor }} />
            </div>
          )}
        </div>
        <div style={{ height: '24px' }} />
      </div>

      <div style={{ padding: '24px 24px 0 24px', textAlign: 'left' }}>
        <Button
          type="primary"
          style={{ 
            fontSize: '20px', 
            height: '54px', 
            width: '100%',
            background: topBackgoundColor, 
            borderColor: topBackgoundColor, 
            borderRadius: '4px',
            color: colorForBackgroundColor(topBackgoundColor),
            pointerEvents: 'none' // Make it non-interactive without altering colors
          }}
        >
          {displayButtonText} <ArrowRightOutlined style={{ color: 'inherit' }} />
        </Button>

        {additionalInfoForTag.length > 0 && (
          <div style={{ marginTop: '24px', padding: '0 8px' }}>
            <Text strong style={{ fontSize: '16px', display: 'block', marginBottom: '12px' }}>
              {signupWarningText}
            </Text>
            {additionalInfoForTag.map((info, index) => {
              const q = typeof info === 'string' ? info : info?.question;
              const a = typeof info === 'string' ? null : info?.answer;
              return (
                <div key={index} style={{ marginBottom: '16px' }}>
                  <Text strong style={{ fontSize: '14px', display: 'block' }}>{q}</Text>
                  {a && <Text style={{ fontSize: '13px', display: 'block', color: 'rgba(0,0,0,0.65)', marginTop: '4px' }}>{a}</Text>}
                </div>
              );
            })}
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: '32px' }}>
          {topLogo ? (
            <img src={topLogo} alt="logo" style={{ maxHeight: '30px' }} />
          ) : (
            <img src={product === 'tipping' ? img_striketip_logo : img_strikepay_logo} alt="logo" style={{ maxHeight: '20px' }} />
          )}
        </div>

        <Text type="secondary" style={{ display: 'block', marginTop: '16px', fontSize: '12px', textAlign: 'center' }}>
          Preview Mode - Buttons are inactive
        </Text>
      </div>
      {showEditModal && (
        <TemplateEditorModal 
          visible={showEditModal} 
          onCancel={() => setShowEditModal(false)} 
          template={template} 
          isAdmin={isAdmin}
        />
      )}
    </Card>
  );
};

export const TemplateList = ({ clientId, isAdmin }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const queryId = clientId || (isAdmin ? 'all' : null);
  const [loading, templates] = usePartnerTemplates(queryId);

  const filteredTemplates = useMemo(() => {
    if (!searchQuery) return templates;
    const q = searchQuery.toLowerCase();
    return (templates || []).filter(t => 
      t.id?.toLowerCase().includes(q) || 
      t.product?.toLowerCase().includes(q) ||
      t.claimType?.toLowerCase().includes(q)
    );
  }, [templates, searchQuery]);

  const columns = [
    {
      title: 'Template ID',
      dataIndex: 'id',
      key: 'id',
      render: (id) => <Text strong>{id}</Text>,
      sorter: (a, b) => a.id.localeCompare(b.id),
      defaultSortOrder: 'ascend',
    },
    {
      // Templates are blueprints: the list always shows BOTH worlds (config
      // never follows the lens) — the badge says which world a template
      // MINTS into. Absence of the flag means live, like everywhere else.
      title: 'Mode',
      dataIndex: 'is_test',
      key: 'is_test',
      render: (isTest) => (isTest
        ? <Tag color={TEST_MODE_CLIENT_COLOR}>TEST</Tag>
        : <Text type="secondary" style={{ fontSize: '11px' }}>live</Text>),
      sorter: (a, b) => (a.is_test === true ? 1 : 0) - (b.is_test === true ? 1 : 0),
    },
    {
      title: 'Claim Type',
      dataIndex: 'claimType',
      key: 'claimType',
      render: (type) => type ? <Tag color="purple">{type}</Tag> : <Text type="secondary" style={{ fontSize: '11px' }}>default</Text>,
      sorter: (a, b) => (a.claimType || '').localeCompare(b.claimType || ''),
    },
    {
      title: 'Product',
      dataIndex: 'product',
      key: 'product',
      render: (p) => <Tag color="blue">{p}</Tag>,
      sorter: (a, b) => (a.product || '').localeCompare(b.product || ''),
    },
    {
      title: 'Links',
      key: 'link',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <a href={`${payerBaseURL}/home/${record.id}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px' }}>
            Home Page <ExportOutlined style={{ fontSize: '10px' }} />
          </a>
          <a href={`https://partner.striketip.com/${record.id}/batch-generator`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px' }}>
            Batch Generator <ExportOutlined style={{ fontSize: '10px' }} />
          </a>
        </Space>
      )
    }
  ];

  return (
    <div style={{ marginTop: '16px' }}>
      <div style={{ marginBottom: '16px' }}>
        <Search
          placeholder="Search by Template ID or Product..."
          allowClear
          onSearch={setSearchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ width: 300 }}
        />
      </div>
      <Table 
        dataSource={filteredTemplates} 
        columns={columns} 
        loading={loading} 
        rowKey="id" 
        size="small" 
        expandable={{
        expandedRowRender: (record) => (
          <div style={{ padding: '24px', background: '#f0f2f5', borderRadius: '8px' }}>
            <Row gutter={[24, 24]} justify="center">
              <Col xs={24} lg={8}>
                <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                  <Badge count="1" style={{ backgroundColor: '#ff0098' }} offset={[10, 0]}>
                    <Title level={5} style={{ margin: 0 }}>Onboarding Screen</Title>
                  </Badge>
                </div>
                <TemplateOnboardPreview template={record} isAdmin={isAdmin} />
              </Col>
              <Col xs={24} lg={8}>
                <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                  <Badge count="2" style={{ backgroundColor: '#52c41a' }} offset={[10, 0]}>
                    <Title level={5} style={{ margin: 0 }}>Link & QR codes</Title>
                  </Badge>
                </div>
                <TemplateLinkPreview template={record} />
              </Col>
              <Col xs={24} lg={8}>
                <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                  <Badge count="3" style={{ backgroundColor: '#1890ff' }} offset={[10, 0]}>
                    <Title level={5} style={{ margin: 0 }}>Payment Screen</Title>
                  </Badge>
                </div>
                <TemplatePaymentPreview template={record} isAdmin={isAdmin} />
              </Col>
            </Row>
          </div>
        ),
        rowExpandable: (record) => true,
        expandIcon: ({ expanded, onExpand, record }) =>
          expanded ? (
            <MinusCircleOutlined onClick={e => onExpand(record, e)} style={{ color: '#ff0098' }} />
          ) : (
            <PlusCircleOutlined onClick={e => onExpand(record, e)} style={{ color: '#ff0098' }} />
          )
      }}
    />
  </div>
  );
};

export const ClaimedTagsList = ({ clientId, isAdmin }) => {
  const [dateRange, setDateRange] = useState([moment().subtract(7, 'days'), moment()]);
  const [templateFilter, setTemplateFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const queryId = clientId || (isAdmin ? 'all' : null);
  const [loading, tags] = usePartnerTags(queryId, dateRange);

  const filteredTags = useMemo(() => {
    let result = (tags || []).filter(tag => ['live', 'paused'].includes(tag.status));
    
    if (templateFilter && templateFilter !== 'all') {
      result = result.filter(tag => tag.templateTagId === templateFilter);
    }
    
    if (statusFilter && statusFilter !== 'all') {
      result = result.filter(tag => tag.status === statusFilter);
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(tag => {
        return (
          tag.id?.toLowerCase().includes(q) ||
          tag.templateTagId?.toLowerCase().includes(q) ||
          tag.teamName?.toLowerCase().includes(q) ||
          tag.displayName?.toLowerCase().includes(q) ||
          tag.email?.toLowerCase().includes(q) ||
          tag.tagUid?.toLowerCase().includes(q)
        );
      });
    }
    
    return result;
  }, [tags, templateFilter, statusFilter, searchQuery]);

  const templateOptions = useMemo(() => {
    const ids = Array.from(new Set((tags || []).filter(tag => ['live', 'paused'].includes(tag.status)).map(t => t.templateTagId).filter(Boolean)));
    const options = ids.sort().map(id => ({ label: id, value: id }));
    return [{ label: 'All Templates', value: 'all' }, ...options];
  }, [tags]);

  const columns = [
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (val) => val ? moment(val.toMillis()).format('YYYY-MM-DD HH:mm') : 'N/A',
      sorter: (a, b) => (a.createdAt?.toMillis() || 0) - (b.createdAt?.toMillis() || 0),
    },
    {
      title: 'Tag / Template',
      key: 'tagTemplate',
      sorter: (a, b) => a.id.localeCompare(b.id),
      render: (_, record) => (
        <Space direction="vertical" size={4}>
          <a href={`${payerBaseURL}/tags/${record.id}`} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 'bold' }}>
            {record.id} <ExportOutlined style={{ fontSize: '12px' }} />
          </a>
          <Space>
            {record.status === 'live' ? 
              <CheckCircleFilled style={{ color: '#52c41a', fontSize: '14px' }} /> : 
              <Tooltip title="Paused">
                <WarningFilled style={{ color: '#faad14', fontSize: '14px' }} />
              </Tooltip>
            }
            <Tag color={record.status === 'live' ? 'green' : 'red'} style={{ margin: 0, fontSize: '11px' }}>{record.templateTagId}</Tag>
          </Space>
        </Space>
      )
    },
    {
      title: 'Team / Client',
      key: 'entity',
      render: (_, record) => {
        const ownerId = record.teamOwnerClientId || record.clientId;
        const teamLink = (isAdmin && record.isTeamTag && record.teamOwnerClientId && record.teamId) ? 
          `/admin/clients/${record.teamOwnerClientId}/teams/${record.teamId}` : null;
        const clientLink = isAdmin ? `/admin/clients/${ownerId}` : null;

        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'start' }}>
            {record.isTeamTag && (
              <div style={{ display: 'flex', gap: '12px', minWidth: '220px', flex: '1' }}>
                <Avatar 
                  shape="square" 
                  size="large"
                  src={record.topLogo || record.avatar} 
                  icon={<TeamOutlined />} 
                  style={{ borderRadius: '8px', flexShrink: 0 }} 
                />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <Text type="secondary" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Team</Text>
                  {teamLink ? (
                    <a href={teamLink} target="_blank" rel="noopener noreferrer">
                      <Text strong style={{ fontSize: '14px', lineHeight: '1.2' }}>{record.teamName || 'Unnamed Team'}</Text>
                    </a>
                  ) : (
                    <Text strong style={{ fontSize: '14px', lineHeight: '1.2' }}>{record.teamName || 'Unnamed Team'}</Text>
                  )}
                  {record.teamDescription && (
                    <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', lineHeight: '1.3', maxWidth: '250px' }}>
                      {record.teamDescription}
                    </Text>
                  )}
                </div>
              </div>
            )}
            <div style={{ minWidth: '200px', flex: '1' }}>
              <Text type="secondary" style={{ fontSize: '10px', textTransform: 'uppercase', display: 'block', marginBottom: '4px', letterSpacing: '0.5px' }}>
                {record.isTeamTag ? 'Claimed By' : 'Client'}
              </Text>
              <ClientInfo clientId={ownerId} href={clientLink} />
            </div>
          </div>
        );
      }
    }
  ];

  return (
    <div style={{ marginTop: '16px' }}>
      <div style={{ marginBottom: '16px', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
        <RangePicker 
          value={dateRange} 
          onChange={setDateRange} 
          ranges={{
            'Today': [moment().startOf('day'), moment().endOf('day')],
            'Last 7 Days': [moment().subtract(7, 'days').startOf('day'), moment().endOf('day')],
            'Last 30 Days': [moment().subtract(30, 'days').startOf('day'), moment().endOf('day')],
            'This Month': [moment().startOf('month'), moment().endOf('month')],
          }}
        />
        <Search
          placeholder="Search team, claimer, email, tag ID..."
          allowClear
          onSearch={setSearchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ width: 300 }}
        />
        <Select 
          placeholder="Filter by Template" 
          style={{ width: 200 }} 
          value={templateFilter}
          onChange={setTemplateFilter}
          options={templateOptions}
        />
        <Select 
          placeholder="Filter by Status" 
          style={{ width: 150 }} 
          value={statusFilter}
          onChange={setStatusFilter}
        >
          <Select.Option value="all">Both</Select.Option>
          <Select.Option value="live">
            <Space>
              <CheckCircleFilled style={{ color: '#52c41a' }} />
              Live
            </Space>
          </Select.Option>
          <Select.Option value="paused">
            <Space>
              <WarningFilled style={{ color: '#faad14' }} />
              Paused
            </Space>
          </Select.Option>
        </Select>
      </div>
      <Table 
        dataSource={filteredTags} 
        columns={columns} 
        loading={loading} 
        rowKey="id" 
        size="small"
      />
    </div>
  );
};


export const DeveloperConfigView = ({ clientId, isAdmin, ...rest }) => {
  const [showModal, setShowModal] = useState(false);
  const [showEventsModal, setShowEventsModal] = useState(false);
  const [configForEditing, setConfigForEditing] = useState(clientId ? { partnerClientId: clientId } : null);
  const [configForEventsEditing, setConfigForEventsEditing] = useState(null);
  const [form] = Form.useForm();
  const [eventsForm] = Form.useForm();
  const [webhookStatus, setWebhookStatus] = useState(undefined);
  const [webhookTypes, setWebhookTypes] = useState(undefined);
  const [webhookConfigId, setWebhookConfigId] = useState(undefined);
  const [is_test, setIsTest] = useState(undefined);
  const client = useClientDetails(clientId);
  const [loading, allWebhookEvents] = useWebookEvents({ partnerClientId: clientId, webhookStatus });
  const webhookConfigs = useWebookConfigs(clientId);

  // Apply local filtering for fields that no longer trigger a new Firestore query
  const webhookEvents = (allWebhookEvents || []).filter(event => {
    const matchesConfig = !webhookConfigId || event.webhookConfigId === webhookConfigId;
    const matchesTestMode = is_test === undefined || !!event.is_test === !!is_test;
    const matchesType = !webhookTypes || (Array.isArray(webhookTypes) && webhookTypes.includes(event.type));
    return matchesConfig && matchesTestMode && matchesType;
  });

  const availableEventTypes = Array.from(new Set([
    ...ALL_WEBHOOK_EVENTS,
    ...(webhookConfigs || [])
      .filter(config => !webhookConfigId || config.id === webhookConfigId)
      .flatMap(config => config.events || []),
  ])).filter(event => WEBHOOK_EVENT_SET.has(event)).sort();

  useEffect(() => {
    if (showModal) {
      form.setFieldsValue(configForEditing || { enabled: true });
    }
  }, [showModal, configForEditing, form]);

  useEffect(() => {
    if (showEventsModal) {
      const normalizedEvents = (configForEventsEditing?.events || []).filter(event => WEBHOOK_EVENT_SET.has(event));
      eventsForm.setFieldsValue({ events: normalizedEvents });
    }
  }, [showEventsModal, configForEventsEditing, eventsForm]);

  const onClickModalCancel = () => {
    form.resetFields();
    setConfigForEditing(null);
    setShowModal(false);
  }

  const onClickEventsModalCancel = () => {
    eventsForm.resetFields();
    setConfigForEventsEditing(null);
    setShowEventsModal(false);
  }

  const modalTitle = (configForEditing ? 'Edit' : 'Add') + ' Webhook Configuration';
  const modalCtaCopy = configForEditing ? 'Update' : 'Create';
  const tabKeyConfig = `${clientId ? clientId : 'strike'}-configs`;
  const tabKeyEvents = `${clientId ? clientId : 'strike'}-events`;
  const tabKeyTemplates = `${clientId ? clientId : 'strike'}-templates`;
  const tabKeyClients = `${clientId ? clientId : 'strike'}-clients`;
  const isDeveloper = (client && client.isDeveloper) ? true : false;

  const createOrUpdateWebhookConfig = (id, values) => {
    const model = new WebhooksModel({ });  
    model.createOrUpdateWebhookConfig(id, values);
  }

  const onFinish = (values) => {
    let valuesUpdated = values;
    if (clientId) valuesUpdated['partnerClientId'] = clientId;
    createOrUpdateWebhookConfig(configForEditing && configForEditing.id, valuesUpdated);
    onClickModalCancel();
  }

  const onEventsFinish = (values) => {
    const normalizedEvents = (values.events || []).filter(event => WEBHOOK_EVENT_SET.has(event));
    createOrUpdateWebhookConfig(configForEventsEditing?.id, { events: normalizedEvents });
    onClickEventsModalCancel();
  }

  return (
    (isDeveloper || isAdmin)  ?
    <>
      <Tabs defaultActiveKey={tabKeyConfig}>
        <TabPane tab="Configuration" key={tabKeyConfig}>
          <WebhookConfigurationList 
            configs={webhookConfigs} 
            setConfigForEditing={setConfigForEditing} 
            setShowModal={setShowModal} 
            setConfigForEventsEditing={setConfigForEventsEditing}
            setShowEventsModal={setShowEventsModal}
            isCreateEnabled={isAdmin}
            isAdmin={isAdmin}
          />
        </TabPane>
        <TabPane tab="Events" key={tabKeyEvents}>
          <>
            <div style={{ textAlign: 'start', margin: '2px', padding: '2px' }}>
              <Space direction="horizontal" wrap>
                <WebhookAppSelector configs={webhookConfigs} value={webhookConfigId} onChange={setWebhookConfigId} />
                <WebhookModeSelector value={is_test} onChange={setIsTest} />
                <WebhookStatusSelector value={webhookStatus} onChange={setWebhookStatus} />
                <WebhookTypesSelector availableTypes={availableEventTypes} value={webhookTypes} onChange={setWebhookTypes} />
              </Space>
            </div>
            <WebhookEventsList events={webhookEvents} loading={loading} />
          </>
        </TabPane>
        <TabPane tab="Templates" key={tabKeyTemplates}>
          <TemplateList clientId={clientId} isAdmin={isAdmin} />
        </TabPane>
        <TabPane tab="Clients" key={tabKeyClients}>
          <ClaimedTagsList clientId={clientId} isAdmin={isAdmin} />
        </TabPane>
      </Tabs>
      <Modal title={modalTitle}
        visible={showModal}
        onCancel={() => onClickModalCancel()}
        footer={null}
        bodyStyle={{ minHeight: '200px', minWidth: '280px' }}
      >
        <div style={{textAlign: 'center'}}>
          <Card>
            <Form
              form={form}
              name="configForm"
              onFinish={onFinish}
              initialValues={configForEditing}
            >
              <Form.Item
                name="description"
                label="Description"
                style={{ width: '100%', textAlign: 'left' }}
              >
                <Input maxLength={64} />
              </Form.Item>
              <Form.Item
                name="url"
                label="Webhook URL"
                style={{ width: '100%', textAlign: 'left' }}
              >
                <Input maxLength={128} />
              </Form.Item>
              <Form.Item
                name="apiKey"
                label="API Key"
                style={{ width: '100%', textAlign: 'left' }}
              >
                <Input.Password maxLength={64} />
              </Form.Item>
              {
                clientId ?
                  <></>
                :
                  <Form.Item
                    name="partnerClientId"
                    label="Partner Client ID"
                    style={{ width: '100%', textAlign: 'left' }}
                  >
                    <Input maxLength={64} />
                  </Form.Item>
              }
              <Form.Item
                name="enabled"
                label="Enabled"
                valuePropName="checked"
                style={{ width: '100%', textAlign: 'left' }}
              >
                <Checkbox />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit">
                  {modalCtaCopy}
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </div>
      </Modal>

      <Modal title="Edit Subscribed Events"
        visible={showEventsModal}
        onCancel={() => onClickEventsModalCancel()}
        footer={null}
        width={600}
      >
        <Form
          form={eventsForm}
          onFinish={onEventsFinish}
        >
          <Form.Item name="events" label="Events">
            <Select 
              mode="multiple" 
              style={{ width: '100%' }}
              placeholder="Select events to subscribe to"
            >
              {buildWebhookEventGroups(ALL_WEBHOOK_EVENTS).map((group) => (
                <Select.OptGroup key={group.family} label={group.family}>
                  {group.events.map((event) => (
                    <Select.Option key={event} value={event}>
                      <Tag color={tagColorForEvent(event)}>{event}</Tag>
                    </Select.Option>
                  ))}
                </Select.OptGroup>
              ))}
            </Select>
          </Form.Item>
          <Form.Item style={{ textAlign: 'right', marginTop: '16px' }}>
            <Button onClick={onClickEventsModalCancel} style={{ marginRight: '8px' }}>Cancel</Button>
            <Button type="primary" htmlType="submit">Save Events</Button>
          </Form.Item>
        </Form>
      </Modal>
    </>

    :
    <Paragraph>
      Contact developers@strikepay.co to activate your developer account.
    </Paragraph>
  );
};

const WebhookConfigurationList = ({ configs, setConfigForEditing, setShowModal, setConfigForEventsEditing, setShowEventsModal, isCreateEnabled, isAdmin }) => {
  const isCreateNewConfigButtonEnabled = !!isCreateEnabled;
  let webhookConfigurationCards = isCreateEnabled ? [<WebhookConfigurationButton key={'0'} setConfigForEditing={setConfigForEditing} setShowModal={setShowModal} />] : [];
  if (configs && configs.length > 0) {
    for (let i = 0; i < configs.length; i++) {
      const config = configs[i];
      webhookConfigurationCards.push(
        <WebhookConfigurationCard 
          config={config} 
          key={i + 1} 
          setConfigForEditing={setConfigForEditing} 
          setShowModal={setShowModal} 
          setConfigForEventsEditing={setConfigForEventsEditing}
          setShowEventsModal={setShowEventsModal}
          isLinkEnabled={isCreateEnabled}
          isAdmin={isAdmin}
        />
      );  
    }
  }

  return webhookConfigurationCards;
}

const addOrEditConfig = (config, setConfigForEditing, setShowModal) => {
  setConfigForEditing(config);
  setShowModal(true);
}

const editEvents = (config, setConfigForEventsEditing, setShowEventsModal) => {
  setConfigForEventsEditing(config);
  setShowEventsModal(true);
}

const WebhookConfigurationButton = ({ setConfigForEditing, setShowModal }) => (
  <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'end', margin: '8px 16px' }}>
      <Button
        style={{ backgroundColor: '#ff0098', color: 'white', margin: '8px' }}
        onClick={() => addOrEditConfig(null, setConfigForEditing, setShowModal)}  
      >Add Webhook Configuration</Button>
  </div>
)

const WebhookConfigurationCard = ({ config, setConfigForEditing, setShowModal, setConfigForEventsEditing, setShowEventsModal, isLinkEnabled, isAdmin }) => {
  const [showKey, setShowKey] = useState(false);
  const prefixURL = `${getAppBaseUrl()}/admin/clients`;
  const partnerClientIdUrl = `${prefixURL}/${config.partnerClientId}`;
  const configEnabled = !!config.enabled;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success('API Key copied to clipboard');
  };

  const title = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Text strong style={{ fontSize: '16px' }}>{config.description || 'No Description'}</Text>
        <Tag color={configEnabled ? 'green' : 'red'}>{configEnabled ? 'Enabled' : 'Disabled'}</Tag>
      </div>
      <Button type="primary" size="small" onClick={() => addOrEditConfig(config, setConfigForEditing, setShowModal)}>Edit</Button>
    </div>
  );

  return (
    <Card title={title} size="small" style={{ marginBottom: '16px', borderRadius: '8px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '4px' }}>
        
        <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', flexWrap: 'wrap', gap: '24px' }}>
          <div style={{ flex: '1 1 200px', textAlign: 'left' }}>
            <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: '4px' }}>APP ID</Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Tag color='blue' style={{ cursor: 'pointer', margin: 0 }} onClick={() => {
                navigator.clipboard.writeText(config.id);
                message.success('APP ID copied');
              }}>{config.id}</Tag>
              <Button 
                type="text" 
                size="small" 
                icon={<CopyOutlined />} 
                onClick={() => {
                  navigator.clipboard.writeText(config.id);
                  message.success('APP ID copied');
                }} 
              />
            </div>
          </div>

          <div style={{ flex: '1 1 320px', textAlign: 'left' }}>
            <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: '4px' }}>API KEY</Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-start' }}>
              <div style={{ width: '220px', overflow: 'hidden' }}>
                <Text code style={{ display: 'inline-block', width: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left' }}>
                  {showKey ? config.apiKey : '•'.repeat(24)}
                </Text>
              </div>
              <Button 
                type="text" 
                size="small" 
                icon={showKey ? <EyeInvisibleOutlined /> : <EyeOutlined />} 
                onClick={() => setShowKey(!showKey)} 
              />
              <Button 
                type="text" 
                size="small" 
                icon={<CopyOutlined />} 
                onClick={() => copyToClipboard(config.apiKey)} 
              />
            </div>
          </div>

          <div style={{ flex: '1 1 300px', textAlign: 'left' }}>
            <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: '4px' }}>WEBHOOK URL</Text>
            <Text code style={{ wordBreak: 'break-all', display: 'block', textAlign: 'left' }}>{config.url}</Text>
          </div>

          <div style={{ flex: '1 1 150px', textAlign: 'left' }}>
            <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: '4px' }}>PARTNER CLIENT ID</Text>
            {isLinkEnabled ? 
              <a href={partnerClientIdUrl} style={{ display: 'block', textAlign: 'left' }}>{config.partnerClientId}</a>
              : <Text style={{ display: 'block', textAlign: 'left' }}>{config.partnerClientId}</Text>
            }
          </div>
        </div>

        <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Text type="secondary" style={{ fontSize: '11px' }}>SUBSCRIBED EVENTS</Text>
            {isAdmin && (
              <Button 
                type="text" 
                size="small" 
                style={{ padding: '0 4px', height: 'auto' }}
                icon={<EditOutlined style={{ fontSize: '12px', color: '#1890ff' }} />}
                onClick={() => editEvents(config, setConfigForEventsEditing, setShowEventsModal)}
              />
            )}
          </div>
          <WebhookConfigEventsList events={config.events} />
        </div>
      </div>
    </Card>
  )
}

const WebhookConfigEventsList = ({ events }) => {
  const sortedEvents = events ? [...events].sort() : [];
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
      {sortedEvents.map((event) => (
        <Tag key={`el-${event}`} color={tagColorForEvent(event)} style={{ margin: '2px' }}>
          {event}
        </Tag>
      ))}
    </div>
  );
} 

export const WebhookEventsList = ({ events, loading }) => {
  const columns = [
    {
      title: 'Date/time',
      dataIndex: 'createdAt',
      key: 'createdAt',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Attempts',
      dataIndex: 'numberOfAttempts',
      key: 'numberOfAttempts',
    },
    {
      title: 'Status',
      dataIndex: 'webhookStatus',
      key: 'webhookStatus',
      width: 120,
    },
  ];

  let eventData = [];

  if(events) {
    for (let i = 0; i < events.length; i++) {
      const event = events[i];
      const createdAt = moment(event.createdAt.toMillis()).format("YYYY-MM-DD HH:mm:ss").toString();
      const numberOfAttempts = event.numberOfAttempts;
      const description = (
        <>
          <b>{event.description ? event.description : ''}</b> - {event.url}
        </>
      );
      const type = <Tag color={tagColorForEvent(event.type)}>{event.type}</Tag>;
      const payload = event.payload ? event.payload : null;
      const { id, tagId, tapId } = event;
      const idEvent = id || tagId || tapId || i;
      const idEventItem = `${event.type}-${idEvent}`;

      const pendingCell = (
        <>
          <Tooltip title={`Due to retry ${event.webhookNextAttemptAt ? 'at ' + moment(event.webhookNextAttemptAt.toMillis()).format("HH:mm") : ''}`}>
            <ClockCircleTwoTone style={{ paddingRight: '16px' }} />
          </Tooltip>
          <Button shape="circle" icon={<RedoOutlined />} size="small" onClick={() => replayWebhook(event.id)} />
        </>
      );

      // The replay button rides EVERY status — a delivered event is still
      // replayable so partners can re-feed their own systems on request.
      const successCell = (
        <>
          <Tooltip title={`Successful ${event.webhookSuccessAt ? 'at ' + moment(event.webhookSuccessAt.toMillis()).format("HH:mm") : ''}`}>
            <CheckCircleFilled style={{ color: 'green', paddingRight: '16px' }} />
          </Tooltip>
          <Button shape="circle" icon={<RedoOutlined />} size="small" onClick={() => replayWebhook(event.id)} />
        </>
      );

      let webhookStatus = (event.webhookStatus === 'success') ? successCell : pendingCell;
      if (event.webhookStatus === 'failed') {
        webhookStatus = (
          <>
            <CloseCircleFilled style={{ color: 'darkred', paddingRight: '16px' }} />
            <Button shape="circle" icon={<RedoOutlined />} size="small" onClick={() => replayWebhook(event.id)} />
          </>
        );
      }
      eventData.push({
        key: idEventItem,
        createdAt,
        description,
        numberOfAttempts,
        type,
        webhookStatus,
        payload,
      });  
    }
  }
  return (
    <Table
      columns={columns}
      dataSource={eventData}
      loading={loading}
      expandable={{
        expandedRowRender: (row) => 
          <Paragraph code style={{ whiteSpace: 'pre-wrap' }}>
            {JSON.stringify(row.payload, null, 2)}
          </Paragraph>,
        rowExpandable: (row) => { return row.payload !== null },
      }}
    />
  );
}

const replayWebhook = async (eventId) => {
  const errorMessage = await WebhookModel.instance().replayWebhook({ eventId });
  if (errorMessage) {
    message.error(`Webhook replay failed — ${errorMessage}`);
  } else {
    message.success('Webhook resent');
  }
}
